import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";

import {
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiPackage,
  FiFileText,
  FiTrash2,
} from "react-icons/fi";

import { toasts } from "../components/AppToast";

// EMPTY FORM

const EMPTY_FORM = {
  mlsec_id: "0",
  mlsec_value: "",
};

export default function ManageInfoinEnglish() {
  // DATA

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // SECTIONS

  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState("0");

  // PAGINATION

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // FORM

  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // DELETE

  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDeleting] = useState(false);

  // MODALS

  const [formModal, setFormModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);

  // FETCH SECTIONS

  const fetchSections = useCallback(async () => {
    try {
      const response = await API.get("/manage-lanaguage/sections");

      const rows = Array.isArray(response.data?.data)
        ? response.data.data
        : [];

      setSections(rows);
    } catch (error) {
      console.error(
        "FETCH ML SECTIONS ERROR:",
        error.response?.data || error.message
      );

      toasts.error(
        error.response?.data?.message || "Failed to load sections"
      );

      setSections([]);
    }
  }, []);

  // FETCH DATA

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await API.get("/manage-lanaguage", {
        params: {
          page,
          limit,
          selectedsec: selectedSection,
        },
      });

      const responseData = response.data;

      const rows = Array.isArray(responseData?.data)
        ? responseData.data
        : [];

      const pagination = responseData?.pagination || {};

      const totalCount = Number(pagination.total || 0);
      const currentPage = Number(pagination.page || page);
      const currentLimit = Number(pagination.limit || limit);

      const pages = Number(
        pagination.totalPages ||
          Math.ceil(totalCount / currentLimit)
      );

      setData(rows);
      setTotal(totalCount);
      setTotalPages(pages);

      if (currentPage !== page) {
        setPage(currentPage);
      }
    } catch (error) {
      console.error(
        "FETCH ML INFORMATION ERROR:",
        error.response?.data || error.message
      );

      toasts.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load ML information"
      );

      setData([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, selectedSection]);

  // LOAD SECTIONS

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  // LOAD DATA

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // SECTION CHANGE

  const handleSectionChange = (e) => {
    const value = e.target.value;

    setSelectedSection(value);
    setPage(1);
  };

  // RESET FORM

  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
    });

    setEditId(null);
  };

  // OPEN ADD MODAL

  const openAddModal = () => {
    resetForm();

    setForm({
      mlsec_id: selectedSection,
      mlsec_value: "",
    });

    setFormModal(true);
  };

  // CLOSE FORM MODAL

  const closeFormModal = () => {
    if (saving) {
      return;
    }

    setFormModal(false);
    resetForm();
  };

  // HANDLE FORM CHANGE

  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // EDIT

  const startEdit = (row) => {
    setForm({
      mlsec_id: String(row.mlsec_id || "0"),
      mlsec_value: row.mlsec_value || "",
    });

    setEditId(row.mlsecval_id);

    setViewModal(null);
    setFormModal(true);
  };

  // VIEW

  const handleView = (row) => {
    setViewModal(row);
  };

  // SUBMIT

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.mlsec_id || form.mlsec_id === "0") {
      toasts.error("Section is required");
      return;
    }

    if (!form.mlsec_value.trim()) {
      toasts.error("Value is required");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        mlsec_id: Number(form.mlsec_id),
        mlsec_value: form.mlsec_value.trim(),
        lang_id: 1,
      };

      let response;

      if (editId) {
        response = await API.put(
          `/manage-lanaguage/${editId}`,
          payload
        );
      } else {
        response = await API.post(
          "/manage-lanaguage",
          payload
        );
      }

      if (response.data?.success) {
        toasts.success(
          response.data.message ||
            (editId
              ? "ML information updated successfully"
              : "ML information added successfully")
        );

        setForm({
          ...EMPTY_FORM,
        });

        setEditId(null);
        setFormModal(false);

        await fetchData();
      } else {
        toasts.error(
          response.data?.message || "Something went wrong"
        );
      }
    } catch (error) {
      console.error(
        editId
          ? "Update ML information error:"
          : "Add ML information error:",
        error.response?.data || error.message
      );

      toasts.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Something went wrong"
      );
    } finally {
      setSaving(false);
    }
  };

  // PREVIOUS PAGE

  const goPrevious = () => {
    if (loading || page <= 1) {
      return;
    }

    setPage((previous) => Math.max(1, previous - 1));
  };

  // NEXT PAGE

  const goNext = () => {
    if (loading || page >= totalPages) {
      return;
    }

    setPage((previous) =>
      Math.min(totalPages, previous + 1)
    );
  };

  // DELETE

  const handleDelete = async () => {
    if (!delModal?.mlsecval_id) {
      return;
    }

    try {
      setDeleting(true);

      const response = await API.delete(
        `/manage-lanaguage/${delModal.mlsecval_id}`
      );

      if (response.data?.success) {
        toasts.success(
          response.data.message || "Deleted successfully"
        );

        setDelModal(null);

        if (data.length === 1 && page > 1) {
          setPage((previous) => previous - 1);
        } else {
          await fetchData();
        }
      } else {
        toasts.error(
          response.data?.message || "Unable to delete"
        );
      }
    } catch (error) {
      console.error(
        "DELETE ML INFORMATION ERROR:",
        error.response?.data || error.message
      );

      toasts.error(
        error.response?.data?.message || "Unable to delete"
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout activePage="ML Information">
      <main className="dashboard-main">
        <div className="table-card">
          {/* HEADER */}

          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiFileText size={26} />

              <span className="ms-2">
                Manage ML Information in English
              </span>
            </h3>

            <select
              name="cmbSection"
              id="cmbSection"
              className="select form-control"
              value={selectedSection}
              onChange={handleSectionChange}
              style={{ maxWidth: "250px" }}
            >
              <option value="0">-- All --</option>

              {sections.map((section) => (
                <option
                  key={section.mlsec_id}
                  value={section.mlsec_id}
                >
                  {section.ml_section}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="um-btn-primary"
              onClick={openAddModal}
            >
              <span className="um-add-icon">＋</span>
              Add Information
            </button>
          </div>

          {/* TABLE */}

          <div className="table-responsive">
            <table className="um-table">
              <thead>
                <tr className="um-table-head">
                  <th className="um-th text-center">#</th>
                  <th className="um-th text-center">
                    Section Name
                  </th>
                  <th className="um-th text-center">Value</th>
                  <th className="um-th text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map(
                    (_, index) => (
                      <tr key={`loading-${index}`}>
                        {Array.from({ length: 4 }).map(
                          (_, cellIndex) => (
                            <td
                              key={`skeleton-${cellIndex}`}
                              className="um-td text-center"
                            >
                              <div className="skeleton" />
                            </td>
                          )
                        )}
                      </tr>
                    )
                  )
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="empty-table-cell text-center py-5"
                    >
                      <div className="empty-icon">
                        <FiPackage />
                      </div>

                      <div className="empty-title">
                        No ML information records found
                      </div>

                      <div className="empty-sub">
                        Use the Add Information button to add
                        information
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => (
                    <tr
                      key={row.mlsecval_id}
                      className={`um-row ${
                        index % 2 === 0
                          ? "even-row"
                          : "odd-row"
                      }`}
                    >
                      {/* NUMBER */}

                      <td className="um-td text-center">
                        {(page - 1) * limit + index + 1}
                      </td>

                      {/* SECTION */}

                      <td className="um-td text-center">
                        <span className="car-name-badge">
                          <strong>
                            {row.ml_section || "—"}
                          </strong>
                        </span>
                      </td>

                      {/* VALUE */}

                      <td
                        className="um-td text-center"
                        style={{
                          textAlign: "left",
                          maxWidth: "500px",
                        }}
                      >
                        {row.mlsec_value || "—"}
                      </td>

                      {/* ACTION */}

                      <td className="um-td text-center">
                        <div className="action-buttons">
                          <button
                            type="button"
                            className="action-btn action-view"
                            title="View Information"
                            onClick={() => handleView(row)}
                          >
                            <FiEye size={15} />
                          </button>

                          <button
                            type="button"
                            className="action-btn action-edit"
                            title="Edit Information"
                            onClick={() => startEdit(row)}
                          >
                            <FiEdit size={15} />
                          </button>

                          <button
                            type="button"
                            className="action-btn action-delete delete-enabled"
                            title="Delete Information"
                            onClick={() => setDelModal(row)}
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* PAGINATION */}

            {total > 0 && (
              <div className="pagination">
                <span className="pagin-info">
                  {`Showing ${(page - 1) * limit + 1}–${Math.min(
                    page * limit,
                    total
                  )} of ${total}`}
                </span>

                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="pg-btn"
                    disabled={page <= 1 || loading}
                    onClick={goPrevious}
                  >
                    ← Prev
                  </button>

                  <span className="pagin-info">
                    Page {page} / {totalPages}
                  </span>

                  <button
                    type="button"
                    className="pg-btn"
                    disabled={
                      page >= totalPages || loading
                    }
                    onClick={goNext}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ADD / EDIT MODAL */}

        {formModal && (
          <div
            className="um-overlay"
            onClick={closeFormModal}
          >
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}

              <div className="um-modal-head">
                <div className="d-flex align-items-center gap-2">
                  <div className="form-card-icon">
                    {editId ? (
                      <FiEdit size={24} />
                    ) : (
                      <FiPlus size={24} />
                    )}
                  </div>

                  <div>
                    <h2 className="um-modal-title mb-0">
                      {editId
                        ? "Edit ML Information"
                        : "Add ML Information"}
                    </h2>
                  </div>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  ✕
                </button>
              </div>

              {/* BODY */}

              <div className="um-modal-scroll">
                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="field-label">
                      Section *
                    </label>

                    <select
                      className="select form-control"
                      value={form.mlsec_id}
                      onChange={(e) =>
                        handleChange(
                          "mlsec_id",
                          e.target.value
                        )
                      }
                    >
                      <option value="0">
                        -- Select Section --
                      </option>

                      {sections.map((section) => (
                        <option
                          key={section.mlsec_id}
                          value={section.mlsec_id}
                        >
                          {section.ml_section}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-12">
                    <label className="field-label">
                      Value *
                    </label>

                    <textarea
                      className="rd-input"
                      style={{
                        textAlign: "start",
                        minHeight: "150px",
                        resize: "vertical",
                      }}
                      placeholder="Enter Value"
                      value={form.mlsec_value}
                      onChange={(e) =>
                        handleChange(
                          "mlsec_value",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              {/* FOOTER */}

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner" />
                      Saving...
                    </>
                  ) : editId ? (
                    <>
                      <FiSave />
                      Update Information
                    </>
                  ) : (
                    <>
                      <FiPlus />
                      Add Information
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* VIEW MODAL */}

        {viewModal && (
          <div
            className="um-overlay"
            onClick={() => setViewModal(null)}
          >
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER */}

              <div className="um-modal-head">
                <div>
                  <h2 className="um-modal-title mb-0">
                    <FiEye /> Information Details
                  </h2>

                  <p className="form-card-sub mb-0">
                    View ML information
                  </p>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={() => setViewModal(null)}
                >
                  ✕
                </button>
              </div>

              {/* BODY */}

              <div className="um-modal-scroll">
                <div className="row g-3">
                  <div className="col-md-12">
                    <div className="form-label">
                      Section Name
                    </div>

                    <div className="form-value">
                      <span className="car-name-badge">
                        {viewModal.ml_section || "—"}
                      </span>
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="form-label">Value</div>

                    <div className="form-value">
                      <div
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {viewModal.mlsec_value || "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* FOOTER */}

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={() => setViewModal(null)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={() => {
                    const selected = viewModal;

                    setViewModal(null);

                    setTimeout(() => {
                      startEdit(selected);
                    }, 100);
                  }}
                >
                  <FiEdit />
                  Edit Information
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DELETE MODAL */}

        {delModal && (
          <div
            className="um-overlay"
            onClick={() => {
              if (!delSaving) {
                setDelModal(null);
              }
            }}
          >
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                <div className="delete-icon">
                  <FiTrash2 size={32} />
                </div>

                <h2 className="delete-title">
                  Delete Information?
                </h2>

                <p className="delete-description">
                  Are you sure you want to delete{" "}
                  <span className="badge badge-success">
                    "
                    {delModal.mlsec_value
                      ? delModal.mlsec_value.substring(
                          0,
                          30
                        ) +
                        (delModal.mlsec_value.length > 30
                          ? "..."
                          : "")
                      : "this record"}
                    "
                  </span>
                  ?
                  <br />
                  This will permanently remove this record.
                  <br />
                  <strong>
                    This action cannot be undone.
                  </strong>
                </p>

                <div className="delete-actions">
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={() => setDelModal(null)}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <>
                        <span className="spinner" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <FiTrash2 size={16} />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </Layout>
  );
}
